import type { JSX } from "preact";
import { useEffect, useState } from "preact/compat";
import { Auth } from "@gss/common/Auth";
import { getError } from "@gss/common/errorUtil";
import { Button } from "@gss/components/Button";
import { AddSquare } from "@gss/components/icons";
import { useMessages } from "@gss/components/Messages";
import { DAO } from "./DAO";
import type { NewsPost, NewsPostNew } from "./news.types";
import { Post, POST_TYPES } from "./Post";

export function News(): JSX.Element {
    const [posts, setPosts] = useState<NewsPostNew[]>([]);
    const [editPost, setEditPost] = useState<NewsPostNew>();
    const { addError } = useMessages();

    const updatePosts = () => {
        return DAO.news
            .get()
            .then((posts) => {
                posts.sort((a, b) => b.created - a.created);
                setPosts(posts);
            })
            .catch(addError);
    };

    useEffect(() => {
        void updatePosts();
    }, []);

    const allowEdit = Auth.isAdmin() && editPost == null;

    const renderAddButton = () => {
        if (!allowEdit) {
            return null;
        }
        return (
            <Button
                variant={null}
                className="p-0 m-0 float-end"
                onClick={() => {
                    const post: NewsPostNew = {
                        title: "",
                        value: [],
                    };
                    setPosts([post, ...posts]);
                    setEditPost(post);
                }}
            >
                <AddSquare className="align-baseline" />
            </Button>
        );
    };

    const savePost = async (post: NewsPostNew) => {
        try {
            if (isNewsPost(post)) {
                await DAO.news.update(post);
            } else {
                await DAO.news.add(post);
            }
            await updatePosts();
            setEditPost(undefined);
        } catch (error: unknown) {
            addError(getError(error));
        }
    };

    return (
        <>
            <div id="news-header">
                <h1>
                    Nyheter
                    {renderAddButton()}
                </h1>
            </div>

            <div id="news-posts">
                {posts.map((post) => (
                    <Post
                        type={POST_TYPES.NEWS}
                        key={post.id ?? -1}
                        post={post}
                        inEditMode={post === editPost}
                        allowEdit={allowEdit}
                        editPost={setEditPost}
                        savePost={(post) => {
                            void savePost(post);
                        }}
                    />
                ))}
            </div>
        </>
    );
}

function isNewsPost(post: NewsPostNew): post is NewsPost {
    return post.id != null;
}
